import React from 'react';
import { Row, Col, Card, Typography, Button, Tag, Timeline } from 'antd';
import { 
  NewspaperOutlined, 
  LinkOutlined, 
  CalendarOutlined,
  TrophyOutlined,
  GlobalOutlined,
  BookOutlined,
  TeamOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const News = () => {
  const newsItems = [
    {
      id: 1,
      title: 'Student Entrepreneurs Expand Access to Quality Education with Offline AI Platform',
      source: 'Yeshiva University News',
      date: 'December 4, 2025',
      type: 'Media Coverage',
      status: 'Featured',
      description: 'Featured in Yeshiva University News for participation in NSF I-Corps program, developing an AI-powered voice agent to help patients search for clinical trials and connecting healthcare innovations with real market needs.',
      highlights: [
        'Selected for prestigious NSF Innovation Corps (I-Corps) program',
        'Developing AI-powered voice agent for clinical trial search and patient matching',
        'Connecting technical healthcare innovations with real market needs',
        'Collaborating with healthcare and biotechnology experts',
        'Working to remove barriers for underserved communities accessing clinical trials'
      ],
      impact: 'The work addresses critical healthcare access challenges, helping underserved and minority communities enroll in life-changing clinical trials through innovative AI technology.',
      url: 'https://www.yu.edu/news/katz/student-entrepreneurs-expand-access-quality-education-offline-ai-platform',
      image: 'https://via.placeholder.com/400x250/667eea/ffffff?text=NSF+I-Corps+Innovation',
      collaborators: ['Ebenezer Andoh (M.S. Biotechnology Management)', 'NSF I-Corps Cohort'],
      tags: ['AI', 'Healthcare Innovation', 'Clinical Trials', 'Entrepreneurship', 'NSF I-Corps', 'Voice Agents']
    },
    {
      id: 2,
      title: 'Cyber Students Build Smarter, Safer Fraud Detection System with AI and Blockchain',
      source: 'Yeshiva University News',
      date: 'September 9, 2025',
      type: 'Research Publication',
      status: 'Featured',
      description: 'Co-authored groundbreaking research combining machine learning and blockchain technology to build an advanced fraud detection system that\'s both accurate and tamper-proof.',
      highlights: [
        'Co-authored study published in Engineering Science & Technology Journal',
        'Developed fraud detection system using Random Forest ML model with nearly perfect accuracy',
        'Integrated blockchain technology for secure, tamper-proof transaction verification',
        'Analyzed over 2,500 financial transactions to identify fraud patterns',
        'Identified key fraud indicators: transaction amount, duration, and customer occupation'
      ],
      impact: 'The research presents practical recommendations for banks and financial institutions to enhance fraud prevention using AI and blockchain technology.',
      url: 'https://www.yu.edu/news/katz/cyber-students-build-smarter-safer-fraud-detection-system-ai-and-blockchain',
      image: 'https://via.placeholder.com/400x250/667eea/ffffff?text=Fraud+Detection+Research',
      collaborators: ['Kumbirai Bernard Muhwati', 'Sarah Mavire'],
      tags: ['Machine Learning', 'Blockchain', 'Cybersecurity', 'Financial Fraud', 'AI']
    },
    {
      id: 3,
      title: 'Graduated from New York I-Corps Hub Regional Lean Bootcamp',
      source: 'New York I-Corps Hub',
      date: 'Summer 2025',
      type: 'Program Completion',
      status: 'Achievement',
      description: 'Successfully completed the intensive entrepreneurship program focused on customer discovery and business model validation through 345 customer interviews.',
      highlights: [
        'Completed comprehensive lean startup methodology training',
        'Conducted extensive customer discovery through 345 interviews',
        'Developed validated business model for innovative solutions',
        'Collaborated with diverse team of entrepreneurs and innovators',
        'Presented final Lessons Learned Presentation to cohort'
      ],
      impact: 'Enhanced entrepreneurial skills and customer discovery methodology, preparing for future innovation and startup ventures.',
      url: 'https://www.linkedin.com/posts/new-york-i-corps-hub-954880240_summer-2025-came-in-hot-and-so-did-our-activity-7362127334906191873-ITz0',
      image: 'https://via.placeholder.com/400x250/764ba2/ffffff?text=I-Corps+Graduation',
      collaborators: ['16 Team Cohort', 'NYC Innovation Hot Spot'],
      tags: ['Entrepreneurship', 'Customer Discovery', 'Lean Startup', 'Innovation', 'Business Development']
    }
  ];

  const researchImpact = [
    {
      metric: '2,500+',
      label: 'Transactions Analyzed',
      description: 'Comprehensive dataset for fraud detection training'
    },
    {
      metric: '99%+',
      label: 'Model Accuracy',
      description: 'Random Forest model performance for fraud detection'
    },
    {
      metric: '345',
      label: 'Customer Interviews',
      description: 'I-Corps program customer discovery research'
    },
    {
      metric: '16',
      label: 'Team Cohort',
      description: 'Entrepreneurs in I-Corps program collaboration'
    }
  ];

  return (
    <section id="news" className="section section-light">
      <div className="container">
        <div className="section-header">
          <Title level={2} className="section-title">Recent News & Achievements</Title>
          <Paragraph className="section-subtitle">
            Latest research publications, program completions, and professional milestones
          </Paragraph>
        </div>

        {/* Main News Articles */}
        <Row gutter={[32, 32]} style={{ marginBottom: '60px' }}>
          {newsItems.map((item, index) => (
            <Col key={item.id} xs={24} lg={12}>
              <Card
                className="fade-in-up"
                style={{ 
                  height: '100%',
                  animationDelay: `${index * 0.1}s`
                }}
                cover={
                  <div style={{
                    height: '200px',
                    background: `linear-gradient(135deg, ${index === 0 ? '#667eea' : index === 1 ? '#764ba2' : '#52c41a'} 0%, ${index === 0 ? '#764ba2' : index === 1 ? '#667eea' : '#1890ff'} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    position: 'relative'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      {index === 0 ? <TeamOutlined style={{ fontSize: '3rem', marginBottom: '16px' }} /> : index === 1 ? <BookOutlined style={{ fontSize: '3rem', marginBottom: '16px' }} /> : <TrophyOutlined style={{ fontSize: '3rem', marginBottom: '16px' }} />}
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                        {index === 0 ? 'Media Coverage' : index === 1 ? 'Research Publication' : 'Program Achievement'}
                      </div>
                    </div>
                  </div>
                }
                title={
                  <div>
                    <Title level={4} style={{ margin: 0, marginBottom: '8px', fontSize: '18px', lineHeight: 1.4 }}>
                      {item.title}
                    </Title>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <Tag color="blue">{item.type}</Tag>
                      <Tag color={index === 0 ? "purple" : index === 1 ? "green" : "orange"}>{item.status}</Tag>
                      <Tag color="purple">{item.date}</Tag>
                    </div>
                  </div>
                }
                extra={
                  <Button
                    type="link"
                    icon={<LinkOutlined />}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ padding: 0 }}
                  >
                    Read More
                  </Button>
                }
              >
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ color: '#1890ff' }}>{item.source}</Text>
                </div>

                <Paragraph style={{ marginBottom: '16px', fontSize: '14px' }}>
                  {item.description}
                </Paragraph>

                <div style={{ marginBottom: '16px' }}>
                  <Title level={5} style={{ marginBottom: '8px' }}>Key Highlights:</Title>
                  <ul style={{ paddingLeft: '20px', marginBottom: '12px' }}>
                    {item.highlights.map((highlight, idx) => (
                      <li key={idx} style={{ marginBottom: '4px', fontSize: '13px' }}>
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ fontSize: '12px' }}>Impact:</Text>
                  <br />
                  <Text style={{ fontSize: '13px', fontStyle: 'italic' }}>
                    {item.impact}
                  </Text>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ fontSize: '12px' }}>Collaborators:</Text>
                  <br />
                  <div style={{ marginTop: '4px' }}>
                    {item.collaborators.map((collaborator, idx) => (
                      <Tag key={idx} size="small" style={{ marginBottom: '4px' }}>
                        {collaborator}
                      </Tag>
                    ))}
                  </div>
                </div>

                <div>
                  <Text strong style={{ fontSize: '12px' }}>Tags:</Text>
                  <div style={{ marginTop: '4px' }}>
                    {item.tags.map((tag, idx) => (
                      <Tag key={idx} size="small" color="blue" style={{ marginBottom: '4px' }}>
                        {tag}
                      </Tag>
                    ))}
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Impact Metrics */}
        <div className="fade-in-up" style={{ animationDelay: '0.2s', marginBottom: '60px' }}>
          <Card style={{ borderRadius: '20px', textAlign: 'center' }}>
            <Title level={3} style={{ marginBottom: '32px', color: '#1890ff' }}>
              Research & Program Impact
            </Title>
            
            <Row gutter={[32, 32]}>
              {researchImpact.map((metric, index) => (
                <Col key={index} xs={24} sm={12} md={6}>
                  <div>
                    <Title level={2} style={{ color: '#1890ff', margin: 0, marginBottom: '8px' }}>
                      {metric.metric}
                    </Title>
                    <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                      {metric.label}
                    </Text>
                    <Text style={{ fontSize: '12px', color: '#666' }}>
                      {metric.description}
                    </Text>
                  </div>
                </Col>
              ))}
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Title level={2} style={{ color: '#1890ff', margin: 0, marginBottom: '8px' }}>
                    3
                  </Title>
                  <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                    Media Features
                  </Text>
                  <Text style={{ fontSize: '12px', color: '#666' }}>
                    Featured in Yeshiva University News articles
                  </Text>
                </div>
              </Col>
            </Row>
          </Card>
        </div>

        {/* Timeline */}
        <div className="fade-in-up" style={{ animationDelay: '0.4s' }}>
          <Card style={{ borderRadius: '20px' }}>
            <Title level={3} style={{ textAlign: 'center', marginBottom: '32px', color: '#1890ff' }}>
              Recent Timeline
            </Title>
            
            <Timeline>
              <Timeline.Item
                color="purple"
                dot={<TeamOutlined />}
              >
                <Card size="small" style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <Title level={5} style={{ margin: 0, flex: 1 }}>
                      Featured in Yeshiva University News - NSF I-Corps Innovation
                    </Title>
                    <Tag color="purple" style={{ marginLeft: '16px' }}>December 2025</Tag>
                  </div>
                  <Paragraph style={{ margin: 0, fontSize: '14px' }}>
                    Highlighted for developing AI-powered voice agent for clinical trial search, participating in NSF I-Corps program to connect healthcare innovations with market needs.
                  </Paragraph>
                </Card>
              </Timeline.Item>

              <Timeline.Item
                color="green"
                dot={<BookOutlined />}
              >
                <Card size="small" style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <Title level={5} style={{ margin: 0, flex: 1 }}>
                      Published Research in Engineering Science & Technology Journal
                    </Title>
                    <Tag color="green" style={{ marginLeft: '16px' }}>September 2025</Tag>
                  </div>
                  <Paragraph style={{ margin: 0, fontSize: '14px' }}>
                    Co-authored groundbreaking research on machine learning and blockchain approaches for fraud prevention in financial transactions.
                  </Paragraph>
                </Card>
              </Timeline.Item>

              <Timeline.Item
                color="blue"
                dot={<TrophyOutlined />}
              >
                <Card size="small">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <Title level={5} style={{ margin: 0, flex: 1 }}>
                      Graduated from New York I-Corps Hub Regional Lean Bootcamp
                    </Title>
                    <Tag color="blue" style={{ marginLeft: '16px' }}>Summer 2025</Tag>
                  </div>
                  <Paragraph style={{ margin: 0, fontSize: '14px' }}>
                    Completed intensive entrepreneurship program with 345 customer interviews and validated business model development.
                  </Paragraph>
                </Card>
              </Timeline.Item>
            </Timeline>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default News;
